import { useChannelStore } from '../../store/channel';
import { useCustomerStore } from '../../store/customer';
import { TRANSLATE_PAYMENT_METHOD } from '../../constants/paymentMethods';
import { toKebabCase, toSnakeCase } from '../../utils/formatters';
import { calcFaceBenefits } from '../../utils/planBenefits';

const getBonusLabel = (productId: number): string => {
  const planBenefits =
    useCustomerStore.getState().customer?.carrierInfo?.planBenefits;
  const { gbAmount } = calcFaceBenefits(planBenefits, productId, true);
  return gbAmount ? `Bônus de ${gbAmount}` : '';
};

// Bônus no formato dos mapas da Cadastra (`{bonus}`: "1gb", "3gb").
export const bonusSlug = (productId: number): string => {
  const planBenefits =
    useCustomerStore.getState().customer?.carrierInfo?.planBenefits;
  const { gbAmount } = calcFaceBenefits(planBenefits, productId, true);
  return gbAmount ? toKebabCase(String(gbAmount)) : '';
};

export const flattenEventData = (
  eventData: Record<string, unknown>,
  prefix = 'event_data_',
): Record<string, unknown> => {
  if (!eventData || typeof eventData !== 'object') return {};

  const flattened = {};
  Object.entries(eventData).forEach(([key, value]) => {
    const newKey = `${prefix}${key}`;
    if (value === null || value === undefined) {
      flattened[newKey] = value;
    } else if (typeof value === 'object') {
      flattened[newKey] = JSON.stringify(value);
    } else {
      flattened[newKey] = String(value);
    }
  });
  return flattened;
};

export const mountLoopEventData = ({
  type,
  msisdn,
  sessionLoopId,
  channelName,
  flattenedEventData,
}: {
  type: string;
  msisdn: string | null;
  sessionLoopId: string | null;
  channelName: string | null;
  flattenedEventData: Record<string, unknown>;
}) => ({
  type,
  msisdn,
  session_id: sessionLoopId,
  tags: {
    channel: 'WEB',
    portal: channelName,
    realm: 'tim_recarga',
    ...flattenedEventData,
  },
});

export const buildGa4Event = ({
  type,
  location,
  label,
  value = '',
  status = 'sucesso',
  error = '',
  errorDetail = '',
  category = '3',
}: {
  type: 'click' | 'view';
  location: string;
  label: string;
  value?: string;
  status?: 'sucesso' | 'falha';
  error?: string;
  errorDetail?: string;
  category?: string;
}): Record<string, unknown> => {
  const currentChannel = useChannelStore.getState().channel;
  return {
    'event-category': category,
    'event-status': status,
    'event-error': error,
    'event-error-detail': errorDetail,
    'event-location': location,
    'hit-type': type === 'click' ? '1' : '2',
    'event-action': type === 'click' ? '2' : '3',
    'event-label': label,
    'event-value': value,
    'channel.name': currentChannel,
  };
};

// Par `nome-do-produto`/`value` dos eventos de dataLayer da recarga, sempre
// derivado do valor em reais.
export const rechargeProductFields = (
  valorProduto: number,
): Record<string, unknown> => ({
  'nome-do-produto': `recarga-${valorProduto}`,
  value: valorProduto,
});

// Envelope dos eventos `select_content` do dataLayer (Mapa de dataLayers –
// MEU TIM). Os três fluxos que o emitem — popup de confirmação (tags 49/50),
// jornada Pix (52–57) e pré-portal (58/59) — variam só nos textos e no
// `id_da_tag`; o resto do payload é fixo e vive aqui.
export const buildSelectContent = ({
  contentType,
  etapa,
  disparos,
  elemento,
  idDaTag,
  valorProduto,
  extra,
}: {
  contentType?: string;
  etapa: string;
  disparos: string;
  elemento?: 'pop-up' | 'botao';
  idDaTag: string;
  /** Omitido nos eventos que não descrevem uma recarga (ex.: o card da programada). */
  valorProduto?: number;
  /** Campos próprios de um item do mapa (ex.: `payment_type`, `tipo_de_falha`). */
  extra?: Record<string, unknown>;
}): Record<string, unknown> => ({
  event: 'select_content',
  ...(contentType && { content_type: contentType }),
  etapa,
  disparos,
  ...(elemento && { elemento }),
  ...(valorProduto !== undefined && rechargeProductFields(valorProduto)),
  ...extra,
  funcionalidade: 'recarga-bemobi',
  webview: 'recarga-bemobi',
  id_da_tag: idDaTag,
});

export const buildCheckoutLocation = (
  isSdkCheckoutEnabled: boolean,
  isCheckoutActive: boolean,
  paymentType: string | null,
): string => {
  const rawLabel = paymentType
    ? (TRANSLATE_PAYMENT_METHOD[paymentType] ?? paymentType)
    : '';
  const suffix = rawLabel ? toSnakeCase(rawLabel) : '';
  if (isSdkCheckoutEnabled) {
    return suffix ? `recarga_bemobi_sdk_${suffix}` : 'recarga_bemobi_sdk';
  }
  if (isCheckoutActive) {
    return suffix ? `recarga_bemobi_iframe_${suffix}` : 'recarga_bemobi_iframe';
  }
  return suffix ? `recarga_bemobi_${suffix}` : 'recarga_bemobi';
};

// Sem event-label no Loop: com vários botões, o `type` diz qual foi clicado.
export type LoopClickType =
  | 'click'
  | 'click_aceite'
  | 'click_negar'
  | 'click_copiar_codigo'
  | 'click_qr_code'
  | 'click_exibir_qr_code'
  | 'click_como_funciona'
  | 'click_voltar'
  | 'click_banner';

type LoopEventType =
  | 'pageview'
  | 'impression'
  | LoopClickType
  | 'click_manter'
  | 'click_upsell'
  | 'click_premiada'
  | 'click_upsell+premiada'
  | 'conversion'
  | 'conversionerror'
  | 'confirmation'
  | 'confirmationprocessing'
  | 'confirmationerror';

// Recebe centavos e omite a tag sem valor, em vez de mandar "0".
export const amountTag = (valueInCents?: number) =>
  valueInCents === undefined ? {} : { transactionAmount: valueInCents / 100 };

export const buildLoopEvent = ({
  type,
  pageName,
  paymentMethod,
  transactionAmount,
}: {
  type: LoopEventType;
  pageName: string;
  paymentMethod?: string;
  transactionAmount?: number;
}): { type: LoopEventType; tags: Record<string, string> } => {
  const tags: Record<string, string> = { page_name: pageName };
  if (paymentMethod) tags.payment_method = paymentMethod;
  if (transactionAmount !== undefined)
    tags.transaction_amount = (transactionAmount * 100).toString();
  return { type, tags };
};

export const productDataGTM = (data: any) => {
  const productId = data?.id || data;
  const formattedValue = productId / 100;
  const bonus = getBonusLabel(productId);

  return {
    nome: `Recarga R$${formattedValue}`,
    valor: formattedValue,
    id: `${data?.productId || data?.id || data}`,
    bonus,
  };
};
